
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Reveal } from '../Reveal';
import { SEO } from '../SEO';
import { FinanceCaseStudies } from '../FinanceCaseStudies';
import {
  Landmark, ShieldAlert, TrendingUp, BadgeEuro,
  Minus, Plus, Calculator, ArrowRight, X, Check, Loader2, Lock, Server, MessageSquare, Cpu, PieChart
} from 'lucide-react';
import { Page } from '../../types';
import { SmartConsultant } from '../SmartConsultant';
import { LeadFormModal } from '../LeadFormModal';

const FinanceTerminal = () => {
  const { t } = useTranslation();
  const [step, setStep] = useState(0);
  useEffect(() => {
    const interval = setInterval(() => setStep((prev) => (prev + 1) % 4), 3000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="relative rounded-xl overflow-hidden shadow-2xl border border-gray-700 bg-[#0F172A] mt-2 h-[320px] flex flex-col">
      <div className="bg-gray-800 px-4 py-2 flex items-center justify-between border-b border-gray-700 shrink-0">
        <span className="ml-4 text-xs font-mono text-gray-400">{t('finance.terminal.log')}</span>
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full bg-cyan-500 animate-pulse"></div>
          <span className="text-[10px] font-mono text-cyan-500 uppercase">{t('finance.terminal.status_monitoring')}</span>
        </div>
      </div>
      <div className="p-6 font-mono text-xs md:text-sm leading-loose overflow-hidden relative flex-1 text-cyan-100">
        <div className="text-gray-500">{t('finance.terminal.transaction')}</div>
        <div className={`transition-opacity duration-500 ${step >= 1 ? 'opacity-100' : 'opacity-0'}`}>
          <div className="text-cyan-400">{t('finance.terminal.analyzing')}</div>
        </div>
        <div className={`transition-opacity duration-500 ${step >= 2 ? 'opacity-100' : 'opacity-0'}`}>
          <div className="text-red-400 mt-2">{t('finance.terminal.risk_alert')}</div>
        </div>
        <div className={`transition-opacity duration-500 mt-4 ${step >= 3 ? 'opacity-100' : 'opacity-0'}`}>
          <div className="bg-cyan-900/20 p-3 rounded border border-cyan-500/20 text-cyan-300">{t('finance.terminal.action')}</div>
        </div>
      </div>
    </div>
  );
};

export const FinancePage: React.FC<{ onNavigate?: (page: Page) => void }> = ({ onNavigate }) => {
  const { t } = useTranslation();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const subSectors = [
    { title: t('finance.sectors.items.01.title'), id: "01", desc: t('finance.sectors.items.01.desc'), highlight: t('finance.sectors.items.01.highlight') },
    { title: t('finance.sectors.items.02.title'), id: "02", desc: t('finance.sectors.items.02.desc'), highlight: t('finance.sectors.items.02.highlight') },
    { title: t('finance.sectors.items.03.title'), id: "03", desc: t('finance.sectors.items.03.desc'), highlight: t('finance.sectors.items.03.highlight') },
    { title: t('finance.sectors.items.04.title'), id: "04", desc: t('finance.sectors.items.04.desc'), highlight: t('finance.sectors.items.04.highlight') },
    { title: t('finance.sectors.items.05.title'), id: "05", desc: t('finance.sectors.items.05.desc'), highlight: t('finance.sectors.items.05.highlight') },
    { title: t('finance.sectors.items.06.title'), id: "06", desc: t('finance.sectors.items.06.desc'), highlight: t('finance.sectors.items.06.highlight') },
    { title: t('finance.sectors.items.07.title'), id: "07", desc: t('finance.sectors.items.07.desc'), highlight: t('finance.sectors.items.07.highlight') },
    { title: t('finance.sectors.items.08.title'), id: "08", desc: t('finance.sectors.items.08.desc'), highlight: t('finance.sectors.items.08.highlight'), variant: 'dark' }
  ];

  return (
    <div className="bg-white min-h-screen pt-24 font-sans text-gray-900">
      <SEO
        title={t('finance.hero.title_line1') + ' ' + t('finance.hero.title_line2')}
        description={t('finance.hero.subtitle')}
        keywords="Finanz KI, Fintech Österreich, Banken Automatisierung"
      />
      <section className="relative pt-20 pb-20 px-6 overflow-hidden bg-white">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-16 items-start">
          <Reveal>
            <div className="pt-4">
              <div className="inline-block px-4 py-2 bg-cyan-50 text-cyan-600 text-xs font-bold uppercase tracking-[0.2em] mb-8 border border-cyan-100">{t('finance.hero.tagline')}</div>
              <h1 className="text-5xl md:text-7xl font-bold tracking-tighter leading-[0.95] mb-8">{t('finance.hero.title_line1')} <br /><span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-600 to-blue-600">{t('finance.hero.title_line2')}</span></h1>
              <p className="text-xl text-gray-500 leading-relaxed mb-10 max-w-lg">{t('finance.hero.subtitle')}</p>
              <div className="flex flex-wrap gap-4 mb-12">
                <button onClick={() => setIsModalOpen(true)} className="bg-cyan-600 text-white px-6 py-4 font-bold uppercase tracking-widest text-xs hover:bg-cyan-700 transition-all shadow-lg rounded-sm">{t('finance.hero.cta_strategy')}</button>
                <button onClick={() => onNavigate && onNavigate('roi')} className="bg-transparent text-gray-900 border border-gray-200 px-6 py-4 font-bold uppercase tracking-widest text-xs hover:border-cyan-600 transition-all rounded-sm flex items-center gap-2"><Calculator size={16} /> {t('finance.hero.cta_roi')}</button>
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.2}><FinanceTerminal /></Reveal>
        </div>
      </section>

      <section className="py-32 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <Reveal>
            <div className="flex flex-col md:flex-row justify-between items-end mb-24 border-b border-gray-900 pb-8">
              <h2 className="text-5xl md:text-7xl font-bold text-gray-900 tracking-tighter leading-none">{t('finance.sectors.title').split(' ')[0]}<br />{t('finance.sectors.title').split(' ')[1]}</h2>
            </div>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 border-t border-l border-gray-200">
            {subSectors.map((sector, idx) => (
              <Reveal key={idx} delay={idx * 0.05}>
                <div
                  onClick={() => setIsModalOpen(true)}
                  className={`group p-12 border-b border-r border-gray-200 transition-all duration-500 min-h-[320px] flex flex-col justify-between relative cursor-pointer overflow-hidden h-full ${sector.variant === 'dark' ? 'bg-[#0B1121] text-white border-gray-800' : 'bg-white hover:bg-cyan-50/30'}`}
                >
                  <div className="absolute top-0 right-0 p-4 opacity-0 group-hover:opacity-100 transition-opacity"><span className={`${sector.variant === 'dark' ? 'text-clever-teal' : 'text-cyan-600'} text-xs font-bold uppercase tracking-widest`}>{sector.highlight}</span></div>
                  <div className={`text-xs font-mono mb-8 ${sector.variant === 'dark' ? 'text-gray-600' : 'text-gray-400'}`}>// {sector.id}</div>
                  <div>
                    <h3 className={`text-3xl font-bold mb-6 group-hover:translate-x-2 transition-transform ${sector.variant === 'dark' ? 'text-white' : 'text-gray-900'}`}>{sector.title}</h3>
                    <p className={`leading-relaxed text-lg ${sector.variant === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>{sector.desc}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-40 bg-[#0B1121] text-white overflow-hidden">
        <div className="max-w-7xl mx-auto px-6">
          <Reveal>
            <div className="text-center mb-20">
              <div className="inline-block px-4 py-2 bg-cyan-900/30 text-cyan-300 text-xs font-bold uppercase tracking-[0.2em] mb-6 border border-cyan-500/30">{t('finance.consultant.tagline')}</div>
              <h2 className="text-4xl md:text-6xl font-bold mb-6 tracking-tight">{t('finance.consultant.title')}</h2>
              <p className="text-gray-400 max-w-2xl mx-auto text-lg">{t('finance.consultant.subtitle')}</p>
            </div>
          </Reveal>
          <Reveal delay={0.2}><SmartConsultant context="finance" title={t('finance.consultant.bot_title')} subtitle={t('finance.consultant.bot_subtitle')} suggestions={t('finance.consultant.suggestions', { returnObjects: true }) as string[]} /></Reveal>
        </div>
      </section>

      <FinanceCaseStudies />

      <LeadFormModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} sector="Finance" />
    </div>
  );
};
